import React, { useCallback, useEffect, useRef, useState } from "react"
import QRCode from "qrcode"

import Button from "@material-ui/core/Button"
import Dialog from "@material-ui/core/Dialog"
import DialogActions from "@material-ui/core/DialogActions"
import DialogContent from "@material-ui/core/DialogContent"
import DialogTitle from "@material-ui/core/DialogTitle"
import CircularProgress from "@material-ui/core/CircularProgress"
import Typography from "@material-ui/core/Typography"
import { makeStyles } from "@material-ui/core/styles"
import WhatsAppIcon from "@material-ui/icons/WhatsApp"

import { RegistrarLogErro } from "../../../../services/clientefiel"
import { gerarCobrancaPix, obterStatusCobrancaPix } from "../../../../services/integracao"
import { useSnackbar } from "../../../../utils/notificacoes"

const STATUS_PAGO = 5
const INTERVALO_POLLING_MS = 5000

/**
 * O backend monta o StatusPedido com String.valueOf(...), então campos nulos podem chegar
 * como a string "null". Normaliza para vazio antes de usar.
 */
const normalizar = valor =>
    typeof valor === "string" && valor !== "null" && valor.trim() !== "" ? valor : ""

/**
 * Popup do painel para gerar e apresentar uma cobrança Pix de um pedido (ex.: pedido lançado como
 * dinheiro/offline que o restaurante quer receber por Pix). Mostra o QR Code + copia-e-cola e envia
 * ao cliente por WhatsApp. Quando o gateway for Pagar.me, também oferece o link de pagamento (cartão).
 * O status de pagamento e a forma do pedido são atualizados pelo backend quando o Pix/link é pago.
 */
export default function DialogCobrancaPix({ open, pedido, onClose }) {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()

    const [cobranca, setCobranca] = useState(null)
    const [qrCodeSrc, setQrCodeSrc] = useState("")
    const [carregando, setCarregando] = useState(false)
    const [erro, setErro] = useState("")
    const [pago, setPago] = useState(false)

    const pagoRef = useRef(false)

    const Authorization = localStorage.getItem("chaveSistema")
    const idPedido = pedido?.id

    const qrCodeText = normalizar(cobranca?.qrCodeText)
    const qrCodeUrl = normalizar(cobranca?.qrCodeUrl)
    const urlLinkCartao = normalizar(cobranca?.urlLinkCartao)
    const telefone = normalizar(cobranca?.telefone)

    // Gera a cobrança assim que o modal abre. Idempotente no backend: se já existe uma cobrança
    // Pix para o pedido, o mesmo QR/copia-e-cola é reapresentado.
    const gerar = useCallback(async () => {
        if (idPedido == null) return
        setCarregando(true)
        setErro("")
        try {
            const resposta = await gerarCobrancaPix(idPedido, Authorization)
            if (!resposta || resposta.retornoErro) {
                const mensagem = resposta?.mensagem ?? "Não foi possível gerar a cobrança Pix."
                setErro(mensagem)
                enqueueSnackbar(mensagem, { variant: "error" })
                return
            }
            setCobranca(resposta)
            if (resposta.statusPagamento === STATUS_PAGO) {
                pagoRef.current = true
                setPago(true)
            }
        } catch (error) {
            RegistrarLogErro(error, "DialogCobrancaPix.gerar", pedido)
            setErro("Não foi possível gerar a cobrança Pix. Tente novamente.")
        } finally {
            setCarregando(false)
        }
        // Authorization vem do localStorage; fora das deps de propósito.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idPedido, enqueueSnackbar])

    useEffect(() => {
        if (!open || idPedido == null) return
        pagoRef.current = false
        setCobranca(null)
        setQrCodeSrc("")
        setErro("")
        setPago(false)
        gerar()
    }, [open, idPedido, gerar])

    // Polling: consulta ativa o gateway; quando pago, o backend concilia o pedido (statusPagamento
    // = PAGO e forma Pix) e o popup avisa o operador.
    useEffect(() => {
        if (!open || idPedido == null) return
        const id = setInterval(async () => {
            if (pagoRef.current) return
            const resposta = await obterStatusCobrancaPix(idPedido, Authorization)
            if (!resposta || resposta.retornoErro) return
            if (resposta.statusPagamento === STATUS_PAGO) {
                pagoRef.current = true
                setPago(true)
                enqueueSnackbar("Pagamento recebido!", { variant: "success" })
            }
        }, INTERVALO_POLLING_MS)
        return () => clearInterval(id)
        // Authorization vem do localStorage; fora das deps de propósito.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, idPedido, enqueueSnackbar])

    // Monta a imagem do QR Code a partir do copia-e-cola (ou usa a imagem do backend, se houver).
    useEffect(() => {
        if (!qrCodeText) {
            setQrCodeSrc("")
            return
        }
        if (qrCodeUrl) {
            setQrCodeSrc(qrCodeUrl)
            return
        }
        QRCode.toDataURL(qrCodeText, { width: 220, margin: 1 }, (err, url) => {
            if (err) {
                RegistrarLogErro(err, "DialogCobrancaPix.gerarQrCode")
                setQrCodeSrc("")
                return
            }
            setQrCodeSrc(url)
        })
    }, [qrCodeText, qrCodeUrl])

    const abrirWhatsapp = texto => {
        if (!telefone) {
            enqueueSnackbar(
                "O pedido não tem telefone do cliente para enviar pelo WhatsApp.",
                { variant: "warning" }
            )
            return
        }
        const url = `https://wa.me/${telefone}?text=${encodeURIComponent(texto)}`
        window.open(url, "_blank", "noopener,noreferrer")
    }

    const handleCopiar = () => {
        navigator.clipboard.writeText(qrCodeText)
        enqueueSnackbar("Código Pix copiado!", { variant: "info" })
    }

    const handleEnviarPix = () => {
        abrirWhatsapp(
            `Olá! Segue o código Pix (copia e cola) para o pagamento do seu pedido:\n\n${qrCodeText}`
        )
    }

    const handleEnviarLink = () => {
        abrirWhatsapp(`Olá! Segue o link para o pagamento do seu pedido:\nLink: ${urlLinkCartao}`)
    }

    const semCobranca = !carregando && !qrCodeText

    return (
        <Dialog
            open={!!open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            onClick={e => e.stopPropagation()}
        >
            <DialogTitle>Cobrança Pix{pago ? " — Pago" : ""}</DialogTitle>
            <DialogContent className={classes.conteudo}>
                {qrCodeSrc && <img src={qrCodeSrc} alt="QR Code Pix" className={classes.qrCode} />}

                {!qrCodeSrc && carregando && <CircularProgress />}

                {pago && (
                    <Typography variant="body1" className={classes.pago}>
                        Pagamento recebido! O status do pedido foi atualizado.
                    </Typography>
                )}

                {!pago && semCobranca && (
                    <Typography variant="body2" color="error" className={classes.texto}>
                        {erro ||
                            "Não foi possível gerar a cobrança Pix deste pedido. Tente novamente."}
                    </Typography>
                )}

                {!pago && qrCodeText && (
                    <Typography variant="body2" className={classes.texto}>
                        Peça para o cliente escanear o QR Code ou envie o código abaixo.
                    </Typography>
                )}

                {qrCodeText && (
                    <React.Fragment>
                        <Button
                            onClick={handleCopiar}
                            variant="outlined"
                            className={classes.botao}
                        >
                            Copiar código
                        </Button>

                        <Button
                            onClick={handleEnviarPix}
                            variant="contained"
                            startIcon={<WhatsAppIcon />}
                            className={classes.botaoWhatsapp}
                            fullWidth
                        >
                            Enviar Pix Copia e Cola por WhatsApp
                        </Button>

                        {urlLinkCartao && (
                            <Button
                                onClick={handleEnviarLink}
                                variant="contained"
                                startIcon={<WhatsAppIcon />}
                                className={classes.botaoWhatsapp}
                                fullWidth
                            >
                                Enviar Link de pagamento
                            </Button>
                        )}

                        {!telefone && (
                            <Typography
                                variant="caption"
                                color="error"
                                className={classes.textoSecundario}
                            >
                                Este pedido não tem telefone do cliente cadastrado para envio por
                                WhatsApp.
                            </Typography>
                        )}
                    </React.Fragment>
                )}

                {!pago && (
                    <Typography variant="caption" className={classes.textoSecundario}>
                        Assim que o pagamento for identificado, o status e a forma de pagamento do
                        pedido são atualizados automaticamente.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

const useStyles = makeStyles(theme => ({
    conteudo: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: theme.spacing(1),
        paddingBottom: theme.spacing(2),
    },
    qrCode: {
        width: 220,
        height: 220,
    },
    texto: {
        marginTop: theme.spacing(1),
    },
    pago: {
        marginTop: theme.spacing(1),
        color: theme.palette.success ? theme.palette.success.main : "#4caf50",
        fontWeight: 600,
    },
    botao: {
        marginTop: theme.spacing(1),
    },
    botaoWhatsapp: {
        marginTop: theme.spacing(1),
        backgroundColor: "#25D366",
        color: "white",
        "&:hover": {
            backgroundColor: "#1da851",
        },
    },
    textoSecundario: {
        marginTop: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
}))
